import moment from "moment";
import { TAX_SLABS } from "./constants";

/** Generate unique ID for line items */
export const generateId = () =>
  `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/**
 * Calculate tax breakdown from inclusive amount (backward calculation)
 */
export const calculateTaxBreakdown = (inclusiveAmount, qty = 1) => {
  const amount = parseFloat(inclusiveAmount) || 0;
  const nights = parseFloat(qty) || 1;

  if (amount === 0) {
    return { base: 0, sgst: 0, cgst: 0, taxRate: 0 };
  }

  const pricePerNight = amount / nights;
  const taxRatePercent =
    pricePerNight < TAX_SLABS.THRESHOLD ? TAX_SLABS.LOW_RATE : TAX_SLABS.HIGH_RATE;

  const baseAmount = amount / (1 + taxRatePercent / 100);
  const totalTax = amount - baseAmount;

  return {
    base: baseAmount,
    sgst: totalTax / 2,
    cgst: totalTax / 2,
    taxRate: taxRatePercent,
  };
};

/**
 * Resolve billing entity from booking (Company > Agent > Guest)
 */
export const resolveBillingEntity = (bookingDetails) => {
  if (!bookingDetails) {
    return { name: "", address: "", email: "", mobile: "", gstin: "" };
  }

  const { company_id, agent_id } = bookingDetails;

  if (company_id?.name) {
    return {
      name: company_id.name,
      address: company_id.address || "",
      email: company_id.email || "",
      mobile: company_id.mobile || "",
      gstin: company_id.gst_number || "",
    };
  }

  if (agent_id?.first_name) {
    return {
      name: `${agent_id.first_name} ${agent_id.last_name || ""}`.trim(),
      address: agent_id.address || "",
      email: agent_id.email || "",
      mobile: agent_id.mobile || "",
      gstin: agent_id.gst_number || "",
    };
  }

  return {
    name: `${bookingDetails.title || ""} ${bookingDetails.name || ""}`.trim(),
    address: bookingDetails.guest_address || "",
    email: bookingDetails.email || "",
    mobile: bookingDetails.guest_mobile || "",
    gstin: "",
  };
};

/**
 * Generate line items from booking rooms
 */
export const generateLineItemsFromBooking = (bookingDetails, gstEnabled = true) => {
  if (!bookingDetails) return [];

  const items = [];

  const rooms = bookingDetails.rooms || [];
  rooms.forEach((room) => {
    const checkIn = moment.unix(room.checkin_date);
    const checkOut = moment.unix(room.checkout_date);
    const nights = checkOut.diff(checkIn, "days") || 1;
    const roomName =
      room.room_id?.roomtype_id?.name || `Room ${room.room_id?.room_no}`;
    const totalInclusive = room.total_price || 0;

    const item = {
      id: generateId(),
      description: `${roomName} (${checkIn.format("DD MMM")} - ${checkOut.format("DD MMM")})`,
      qty: nights,
      inclusiveRate: (totalInclusive / nights).toFixed(2),
      inclusiveAmount: totalInclusive,
    };

    if (gstEnabled) {
      Object.assign(item, calculateTaxBreakdown(totalInclusive, nights));
    } else {
      item.base = totalInclusive.toFixed(2);
      item.sgst = "0.00";
      item.cgst = "0.00";
      item.taxRate = 0;
    }

    items.push(item);
  });

  const roomsTotal = items.reduce(
    (sum, item) => sum + Number(item.inclusiveAmount),
    0
  );
  const bookingTotal =
    bookingDetails.custom_price || bookingDetails.total_price || 0;

  if (bookingTotal > roomsTotal) {
    const adjustment = bookingTotal - roomsTotal;
    const adjustmentItem = {
      id: generateId(),
      description: "Service Charges / Adjustments",
      qty: 1,
      inclusiveRate: adjustment.toFixed(2),
      inclusiveAmount: adjustment,
    };

    if (gstEnabled) {
      Object.assign(adjustmentItem, calculateTaxBreakdown(adjustment, 1));
    } else {
      adjustmentItem.base = adjustment.toFixed(2);
      adjustmentItem.sgst = "0.00";
      adjustmentItem.cgst = "0.00";
      adjustmentItem.taxRate = 0;
    }

    items.push(adjustmentItem);
  }

  return items;
};

/**
 * ADAPTER: Normalizes Backend API Preview Data for the InvoicePDF Component
 */
export const normalizePdfData = (apiData, hotelProfile) => {
  if (!apiData) return null;

  // Ensure dates are unix timestamps (seconds) as expected by InvoicePDF
  const toUnix = (dateVal) => {
    if (!dateVal) return Math.floor(Date.now() / 1000);
    return typeof dateVal === "number"
      ? dateVal
      : Math.floor(new Date(dateVal).getTime() / 1000);
  };

  // Sort Room Charges by Date
  const sortedCharges = [...(apiData.room_charges || [])].sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );

  return {
    ...apiData,
    inv_no: apiData.inv_no || "DRAFT", // Enforce Draft status visually if missing
    checkin_date: toUnix(apiData.checkin_date),
    checkout_date: toUnix(apiData.checkout_date),
    inv_date: toUnix(apiData.inv_date),
    room_charges: sortedCharges,
    
    // Attach Hotel Profile for Header/Footer (Required by InvoicePDF)
    hotelDetails: {
      name: hotelProfile?.name || "",
      address: hotelProfile?.address || "",
      phone: hotelProfile?.phone || "",
      mobile: hotelProfile?.mobile || "",
      email: hotelProfile?.email || "",
      gstin: hotelProfile?.gstin || "",
      logo: hotelProfile?.logo || "",
      bank_details: hotelProfile?.bank_details || {},
      cin: hotelProfile?.cin || "",
      pan: hotelProfile?.pan || "",
      gst_enabled: hotelProfile?.gst_enabled || false,
    },
  };
};